import { useEffect, useRef } from 'react';

/**
 * Polls `callback` on an interval, pausing while the browser tab is hidden and firing an
 * immediate refresh when it becomes visible again (avoids wasting request units on a
 * backgrounded tab). Does not call `callback` on mount — callers own their initial load.
 *
 * `enabled` (default true) turns the whole mechanism off: no interval, no visibility listener.
 * A caller whose callback simply returns early still pays for a timer waking up and a listener
 * per mounted component — the Lens document view has nothing to poll at all, so it passes false.
 */
export function useVisibilityPolling(
  callback: () => void,
  intervalMs: number,
  enabled: boolean = true
): void {
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  useEffect(() => {
    if (!enabled) return;

    let intervalId: ReturnType<typeof setInterval> | null = null;

    const startPolling = () => {
      if (intervalId) return;
      intervalId = setInterval(() => callbackRef.current(), intervalMs);
    };
    const stopPolling = () => {
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };
    const handleVisibility = () => {
      if (document.hidden) {
        stopPolling();
      } else {
        callbackRef.current();
        startPolling();
      }
    };

    startPolling();
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      stopPolling();
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [intervalMs, enabled]);
}
